// Components/Admin/Users/Staff/AddUser.js
import React, { useEffect, useState, useMemo } from "react";
import ImageUpload from "../../Addmission/Components/ImageUpload";
import { FiLoader } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import {
  addUser,
  editUser,
} from "../../../../Store/Slices/Admin/Users/Staff/staff.action";
import {
  getAllRolesThunk,
  assignRoleThunk,
} from "../../../../Store/Slices/Common/RBAC/rbacThunks";
import { motion } from "framer-motion";
import { toast } from "react-hot-toast";
import ProtectedSection from "../../../../Routes/ProtectedRoutes/ProtectedSection";
import { PERMISSIONS } from "../../../../config/permission";

// ----- Ant Design -----
import { Upload, Button, Select, Input, Row, Col } from "antd";
import {
  UploadOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";

// ----- Fuzzy Search -----
import Fuse from "fuse.js";
import { useTranslation } from "react-i18next";
import useCloudinaryUpload from "../../../../Hooks/CommonHooks/useCloudinaryUpload";
import Spinner from "../../../../Components/Common/Spinner";
import { bloodGroupOptions } from "../../Addmission/AdminAdmission/Configs/selectOptionsConfig";

const { Option } = Select;

const AddUser = ({ role, data, onClose }) => {
  const dispatch = useDispatch();
  const { roles } = useSelector((store) => store.admin.rbac);
  const { loading, error } = useSelector((store) => store.admin.all_staff);
  const { t } = useTranslation("admUserProfile");
  const [imagePreview, setImagePreview] = useState(null);
  const [isLoading,setIsLoading] = useState(false)
  const [address, setAddress] = useState({
    street: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
  });
  const [teacherData, setTeacherData] = useState({
    firstName: "",
    lastName: "",
    dob: "",
    religion: "",
    gender: "",
    // monthlySalary: "",
    // departmentName: "",
    bloodGroup: "",
    mobileNumber: "",
    email: "",
    profile: null,
    teacherCV: null,
    employeeID: "",
    role: "",
    active: true,
  });

  // Role states
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedRolePermissions, setSelectedRolePermissions] = useState([]);
  const [documents, setDocuments] = useState([{ name: "", file: null }]);
  // For fuzzy search
  const [allRoleItems, setAllRoleItems] = useState([]); // Full list of roles
  const [filteredRoleItems, setFilteredRoleItems] = useState([]); // Search-filtered roles
  const { roles: AllRoles } = useSelector((state) => state.admin.rbac);
  const departments = AllRoles?.map((dept) => dept.department.toLowerCase());

  useEffect(() => {
    dispatch(getAllRolesThunk());
  }, []);

  const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_PRESET;
  const CLOUDINARY_FOLDER = "staff";
  const { uploadFile } = useCloudinaryUpload(
    CLOUDINARY_UPLOAD_PRESET,
    CLOUDINARY_FOLDER
  );
  
  useEffect(() => {
    if (error?.message) toast.error(error.message);
  }, [error]);

  useEffect(() => {
    dispatch(getAllRolesThunk());
  }, [dispatch]);

  useEffect(() => {
    const flatRoles = roles?.flatMap((dept) => dept.roles) || [];
    setAllRoleItems(flatRoles);
    setFilteredRoleItems(flatRoles);
  }, [roles]);

  useEffect(() => {
    if (data) {
      setTeacherData({
        firstName: data.firstName || "",
        lastName: data.lastName || "",
        dob: data.dob ? new Date(data.dob).toISOString().split("T")[0] : "",
        religion: data.religion || "Islam",
        gender: data.gender || "",
        // monthlySalary: data.monthlySalary || "",
        // departmentName: data.departmentName || "",
        bloodGroup: data.bloodGroup || "",
        mobileNumber: data.mobileNumber || "",
        email: data.email || "",
        profile: null,
        teacherCV: null,
        employeeID: data.employeeID || "",
        role: "",
        active: data.active ?? true,
      });

      setAddress({
        street: data.address?.street || "",
        city: data.address?.city || "",
        state: data.address?.state || "",
        postalCode: data.address?.postalCode || "",
        country: data.address?.country || "",
      });

      setImagePreview(data.profile || null);
      setDocuments(data.documents || [{ name: "", file: null }]);
      // Pre-select existing roles
      const existingRoleNames = Array.isArray(data.position)
        ? data.position
        : data.position
        ? [data.position]
        : [];
      const initialSelectedRoles =
        allRoleItems
          ?.filter((r) => existingRoleNames.includes(r.name))
          .map((r) => ({ id: r.id, name: r.name, permission: r.permission })) ||
        [];
      setSelectedRoles(initialSelectedRoles);
      setSelectedRolePermissions([...new Set(data.permission || [])]);
    } else {
      resetForm();
    }
  }, [data, role, allRoleItems]);

  /* ------------------------------------------------------------------
     Helpers
  ------------------------------------------------------------------*/
  const resetForm = () => {
    setTeacherData({
      firstName: "",
      lastName: "",
      dob: "",
      religion: "",
      gender: "",
      // monthlySalary: "",
      // departmentName: "",
      bloodGroup: "",
      mobileNumber: "",
      email: "",
      profile: null,
      teacherCV: null,
      employeeID: "",
      role: role || "",
      active: true,
    });
    setAddress({
      street: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
    });
    setSelectedRoles([]);
    setSelectedRolePermissions([]);
    setImagePreview(null);
    setDocuments([{ name: "", file: null }]);
  };

  const genderOptions = [
    { value: "male", label: t("Male") },
    { value: "female", label: t("Female") },
    { value: "other", label: t("Other") },
  ];
  const religionOptions = [
    { value: "Islam", label: t("Islam") },
    { value: "Christianity", label: t("Christianity") },
    { value: "Hinduism", label: t("Hinduism") },
    { value: "Other", label: t("Other") },
  ];

  // Generic input handler (works for Ant Input + native inputs)
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTeacherData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddressInputChange = (e) => {
    const { name, value } = e.target;
    setAddress((prev) => ({ ...prev, [name]: value }));
  };

  /* ------------------------- Profile Image ------------------------- */
  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
      setTeacherData((prev) => ({ ...prev, profile: file }));
    }
  };

  /* ---------------------------- CV Upload --------------------------- */
  const handleCVBeforeUpload = (file) => {
    if (file.type !== "application/pdf") {
      toast.error(t("Please upload a PDF file."));
      return Upload.LIST_IGNORE;
    }
    setTeacherData((prev) => ({ ...prev, teacherCV: file }));
    return false; // stop auto-upload
  };

  const handleRemoveFile = () =>
    setTeacherData((prev) => ({ ...prev, teacherCV: null }));

  const handlePreviewFile = (file) => {
    const fileURL = URL.createObjectURL(file.originFileObj);
    window.open(fileURL, "_blank");
  };

  /* ------------------------- Fuzzy-Search Roles ---------------------- */
  const fuse = useMemo(
    () =>
      new Fuse(allRoleItems, {
        keys: ["name"],
        threshold: 0.3,
        includeMatches: true,
      }),
    [allRoleItems]
  );

  const highlightText = (text, matchedIndices) => {
    if (!matchedIndices?.length) return text;
    const res = [];
    let last = 0;
    matchedIndices.forEach(([start, end]) => {
      if (start > last) res.push(text.slice(last, start));
      res.push(
        <mark key={`${start}-${end}`} className="bg-yellow-200">
          {text.slice(start, end + 1)}
        </mark>
      );
      last = end + 1;
    });
    if (last < text.length) res.push(text.slice(last));
    return <>{res}</>;
  };

  const handleRoleSearch = (value) => {
    if (!value) return setFilteredRoleItems(allRoleItems);
    const results = fuse.search(value).map((r) => ({
      ...r.item,
      highlightIndices: r.matches?.[0]?.indices || [],
    }));
    setFilteredRoleItems(results);
  };

  const handleRoleChange = (selectedNames) => {
    const matched = allRoleItems
      .filter((r) => selectedNames.includes(r.name))
      .map((r) => ({ id: r.id, name: r.name, permission: r.permission }));
    setSelectedRoles(matched);
    setSelectedRolePermissions([
      ...new Set(matched.flatMap((r) => r.permission)),
    ]);
  };

  /* ------------------------- Document Handlers ---------------------- */
  const handleDocumentNameChange = (index, e) => {
    const { value } = e.target;
    setDocuments((prevDocs) =>
      prevDocs.map((doc, i) => (i === index ? { ...doc, name: value } : doc))
    );
  };

  // Handle file change (Ant Design Upload)
  const handleDocumentFileChange = (index, info) => {
    const file = info.fileList[0]?.originFileObj || null;
    setDocuments((prevDocs) =>
      prevDocs.map((doc, i) => (i === index ? { ...doc, file } : doc))
    );
  };

  // Add new document field
  const handleAddDocument = () => {
    setDocuments((prevDocs) => [
      ...prevDocs,
      { name: "", file: null, url: "" },
    ]);
  };

  // Remove a document field
  const handleRemoveDocument = (index) => {
    setDocuments((prevDocs) => prevDocs.filter((_, i) => i !== index));
  };

  /* ---------------------------- Submit ------------------------------ */
  const handleSubmit = async (e) => {
    e.preventDefault();
setIsLoading(true)
    if (!teacherData.firstName.trim())
      return toast.error(t("First name is required."));
    if (!teacherData.email?.includes("@"))
      return toast.error(t("Please enter a valid email address."));

    const selectedRoleIds = selectedRoles.map((r) => r.id);
    const permissions = selectedRolePermissions;

    try {
      // Separate existing docs (already have a URL) and new ones (need upload)
      const existingDocs = documents.filter((doc) => doc.url);
      const newDocs = documents.filter((doc) => doc.file);

      // Validate document names before upload
      for (const doc of newDocs) {
        if (!doc.name.trim()) {
          return toast.error(
            t("Document name is required for all uploaded files.")
          );
        }
      }

      // Upload new documents
      const uploadedDocs = await Promise.all(
        newDocs.map(async (doc) => {
          const url = await uploadFile(doc.file);
          
          return { name: doc.name, url };
        })
      );
      let profileUrl = data?.profile || null;

      if (teacherData.profile) {
        profileUrl = await uploadFile(teacherData.profile); // Upload to Cloudinary
        
      }

      // Final document list
      const finalDocuments = [...existingDocs, ...uploadedDocs];

      // Create payload
      const payload = {
        ...teacherData,
        address,
        role:"staff",
        profile: profileUrl,
        documents: finalDocuments,
      };

      // --- Dispatch logic ---
      if (data) {
        // Update user
        await dispatch(
          editUser({ userData: payload, address, id: data._id })
        ).unwrap();

        // Assign roles
        await dispatch(
          assignRoleThunk({
            staffId: data._id,
            roleId: selectedRoleIds,
            permission: permissions,
          })
        ).unwrap();
        toast.dismiss();
        toast.success(t("User updated and roles assigned successfully!"));
        onClose();
      } else {
        // Create new user
        const res = await dispatch(
          addUser({ userData: payload })
        ).unwrap();

        if (res?.success) {
          resetForm();
          onClose();
        }
        //  else {
        //   toast.error(t("Failed to add user."));
        // }
      }
      
    } catch (err) {
      console.error(err);
      toast.dismiss();
      toast.error(t("Something went wrong. Please try again."));
    } finally {
      setIsLoading(false)
    }
  }

  /* ========================== RENDER ========================== */
  const [selectTag, setSelectTag] = useState(false);
  return (
    <motion.div
      className="p-6 bg-white  rounded-lg overflow-auto"
      style={{ maxHeight: "90vh" }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <form className="space-y-8" onSubmit={handleSubmit}>
        {/* ───── Top Section ───── */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          {/* Avatar */}
          <div className="col-span-1 flex justify-center mt-5">
            <ImageUpload
              imagePreview={imagePreview}
              handleImageChange={handleImageChange}
              handleRemoveImage={() => {
                setImagePreview(null);
                setTeacherData((p) => ({ ...p, profile: null }));
              }}
              height="h-80"
              width="w-80"
            />
          </div>

          {/* Left column */}
          <div className="col-span-1 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t("First Name")}
              </label>
              <Input
                name="firstName"
                value={teacherData.firstName}
                onChange={handleInputChange}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t("Last Name")}
              </label>
              <Input
                name="lastName"
                value={teacherData.lastName}
                onChange={handleInputChange}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t("Date of Birth")}
              </label>
              <Input
                type="date"
                name="dob"
                value={teacherData.dob}
                onChange={handleInputChange}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t("Religion")}
              </label>
              <Select
                value={teacherData.religion}
                onChange={(value) =>
                  setTeacherData((p) => ({ ...p, religion: value }))
                }
                placeholder={t("Select Religion")}
                style={{ width: "100%" }}
              >
                {religionOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            </div>
          </div>

          {/* Right column */}
          <div className="col-span-1 space-y-6">
            {/* <div>
              <label className="block text-sm font-medium text-gray-700">
                {t("Department")}
              </label>
              <Select placeholder="Select department" className="rounded-lg w-full" value={teacherData.departmentName}
                onChange={(value) =>
                  setTeacherData((p) => ({ ...p, departmentName: value }))
                }
              >
                {departments?.map((dep) => (
                  <Option key={dep} value={dep}>
                    {dep}
                  </Option>
                ))}
              </Select>
            </div> */}

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t("Employee ID")}
              </label>
              <Input
                name="employeeID"
                value={teacherData.employeeID}
                onChange={handleInputChange}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t("Blood Group")}
              </label>
              <Select
                value={teacherData.bloodGroup}
                onChange={(value) =>
                  setTeacherData((p) => ({ ...p, bloodGroup: value }))
                }
                placeholder={t("Select Blood Group")}
                style={{ width: "100%" }}
              >
                {bloodGroupOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t("Gender")}
              </label>
              <Select
                value={teacherData.gender}
                onChange={(value) =>
                  setTeacherData((p) => ({ ...p, gender: value }))
                }
                placeholder={t("Select Gender")}
                style={{ width: "100%" }}
                required
              >
                {genderOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            </div>
          </div>
        </motion.div>

        {/* ───── Contact Information ───── */}
        <div>
          <h2 className="text-xl font-semibold">{t("Contact Information")}</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t("Mobile Number")}
              </label>
              <Input
                name="mobileNumber"
                value={teacherData.mobileNumber}
                onChange={handleInputChange}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t("Email")}
              </label>
              <Input
                name="email"
                value={teacherData.email}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          {/* ───── Address ───── */}
          <h2 className="text-xl font-semibold pt-8">{t("Address")}</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 mb-3">
            <div>
              <label className="block  text-sm font-medium text-gray-500">
                {t("Country")}
              </label>
              <Input
                name="country"
                value={address.country}
                onChange={handleAddressInputChange}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">
                {t("State")}
              </label>
              <Input
                name="state"
                value={address.state}
                onChange={handleAddressInputChange}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-3">
            <div>
              <label className="block text-sm font-medium text-gray-500">
                {t("City")}
              </label>
              <Input
                name="city"
                value={address.city}
                onChange={handleAddressInputChange}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">
                {t("Postal Code")}
              </label>
              <Input
                name="postalCode"
                value={address.postalCode}
                onChange={handleAddressInputChange}
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-500">
              {t("Street")}
            </label>
            <Input
              name="street"
              value={address.street}
              onChange={handleAddressInputChange}
              required
            />
          </div>

          {/* ───── CV Upload ───── */}
          <div className="space-y-4 mt-6">
            <label className="block text-sm font-medium text-gray-700 my-2">
              {t("Upload CV (PDF)")}:
            </label>
            <Upload
              accept=".pdf"
              beforeUpload={handleCVBeforeUpload}
              onRemove={handleRemoveFile}
              onPreview={handlePreviewFile}
              showUploadList={
                teacherData.teacherCV
                  ? {
                      showRemoveIcon: true,
                      showPreviewIcon: true,
                    }
                  : false
              }
              fileList={
                teacherData.teacherCV
                  ? [
                      {
                        uid: "-1",
                        name: teacherData.teacherCV.name,
                        status: "done",
                        originFileObj: teacherData.teacherCV,
                      },
                    ]
                  : []
              }
            >
              <Button icon={<UploadOutlined />}>{t("Select PDF")}</Button>
            </Upload>
          </div>
        </div>

        {/* ───── Documents Upload Section ───── */}
        <motion.div
          className="space-y-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          <h2 className="text-xl font-semibold  text-gray-800 ">
            {t("Documents")}
          </h2>

          <div className="space-y-4">
            {documents.map((doc, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white  p-4 rounded-2xl shadow-sm border border-gray-200  hover:shadow-md transition-all duration-200"
              >
                <Row gutter={[16, 16]} align="middle">
                  <Col xs={24} sm={10}>
                    <Input
                      size="large"
                      placeholder={t(
                        "Document Name (e.g., Passport, Certificate)"
                      )}
                      value={doc.name}
                      onChange={(e) => handleDocumentNameChange(index, e)}
                      required
                      className="rounded-xl"
                    />
                  </Col>

                  <Col xs={24} sm={10}>
                    <Upload
                      name="file"
                      beforeUpload={() => false}
                      onChange={(info) => handleDocumentFileChange(index, info)}
                      maxCount={1}
                      fileList={
                        doc.file
                          ? [
                              {
                                uid: doc.file.uid,
                                name: doc.file.name,
                                status: "done",
                              },
                            ]
                          : []
                      }
                      className="w-full"
                    >
                      <Button
                        size="large"
                        icon={<UploadOutlined />}
                        className="rounded-xl w-full"
                      >
                        {t("Select File")}
                      </Button>
                    </Upload>
                  </Col>

                  <Col xs={24} sm={4} className="flex justify-end">
                    <Button
                      danger
                      shape="circle"
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveDocument(index)}
                      // disabled={documents.length <= 1}
                      className="shadow-sm hover:scale-110 transition-transform duration-200"
                    />
                  </Col>
                </Row>
              </motion.div>
            ))}
          </div>

          <div className="pt-2">
            <Button
              type="dashed"
              onClick={handleAddDocument}
              block
              icon={<PlusOutlined />}
              className="rounded-xl py-5 font-medium border-gray-400 hover:border-gray-600  "
            >
              {t("Add More Documents")}
            </Button>
          </div>
        </motion.div>

        {/* ───── Role Assignment (edit only) ───── */}
        {data && (
          <motion.div
            className="space-y-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
          >
            <ProtectedSection
              requiredPermission={PERMISSIONS.ASSIGN_ROLE}
              title={"Assign Role"}
            >
              <h2 className="text-xl font-semibold">{t("Role Assignment")}</h2>
              <p className="text-xs text-green-600 mb-2">
                {t(
                  "You can select multiple roles below (fuzzy search enabled):"
                )}
              </p>
              <Select
                mode="multiple"
                allowClear
                style={{ width: "100%" }}
                placeholder={t("Search & Select Roles")}
                value={selectedRoles.map((r) => r.name)}
                onChange={handleRoleChange}
                filterOption={false}
                open={selectTag}
                onClick={() => setSelectTag(!selectTag)}
                onSearch={handleRoleSearch}
              >
                {filteredRoleItems.map((roleItem) => (
                  <Option key={roleItem.id} value={roleItem.name}>
                    {highlightText(roleItem.name, roleItem.highlightIndices)}
                  </Option>
                ))}
              </Select>
            </ProtectedSection>
          </motion.div>
        )}

        {/* ───── Submit Button ───── */}
        <motion.div
          className="flex justify-end mt-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.5 }}
        >
          <motion.button
            type="submit"
            disabled={isLoading}
            className={`${
              loading
                ? "bg-gray-400 cursor-not-allowed"
                : "btn-gradient"
            } text-white text-lg py-3 px-8 rounded-md flex items-center justify-center transition-transform duration-200`}
            aria-label={data ? t("Update User") : t("Add New User")}
          >
            {isLoading
              ? "...Updating"
              : data
              ? t("Update User")
              : t("Add New User")}
          </motion.button>
        </motion.div>
      </form>
    </motion.div>
  );
};

export default AddUser;
